import { memo } from "react";
import { SmoothLink as Link } from "@/components/navigation";
import { Badge } from "@/components/ui/badge";
import { Building2, MapPin, Star, Award } from "lucide-react";
import type { Hospital } from "@/types/schema";
import { cn } from "@/lib/utils";
import { getOptimizedImageUrl } from "@/lib/image-utils";
import { useAnalyticsContext } from "@/components/common/AnalyticsProvider";

interface HospitalCardProps {
  hospital: Hospital;
  className?: string;
  compact?: boolean;
}

export const HospitalCard = memo(function HospitalCard({ hospital, className, compact = false }: HospitalCardProps) {
  const { trackHospitalView } = useAnalyticsContext();
  return (
    <Link 
      to={`/hospitals/${hospital.slug}`} 
      className="block"
      onClick={() => trackHospitalView(hospital.name, hospital.city)}
    >
      <div className={cn(
        "flex items-start sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border hover:border-primary hover:shadow-sm transition-all bg-card",
        className
      )}>
        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
          {hospital.logo_url || hospital.image_url ? (
            <img 
              width={100}
              height={100}
              src={getOptimizedImageUrl(hospital.logo_url || hospital.image_url, 100, 100)} 
              alt={hospital.name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-secondary" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-secondary truncate text-sm sm:text-base">
                {hospital.name}
              </h3>
              <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-muted-foreground">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{hospital.city}</span>
              </div>
            </div>
            
            {/* Rating - Mobile optimized */}
            <div className="text-right flex-shrink-0">
              <div className="flex items-center gap-0.5 sm:gap-1 text-accent">
                <Star className="w-3 h-3 sm:w-4 sm:h-4 fill-accent" />
                <span className="font-semibold text-sm sm:text-base">{hospital.rating}</span>
              </div>
              <p className="text-[10px] sm:text-xs text-muted-foreground">
                {hospital.review_count} reviews
              </p>
            </div>
          </div>
          
          {!compact && hospital.accreditations && hospital.accreditations.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5 sm:mt-2">
              {hospital.accreditations.slice(0, 2).map((acc) => (
                <Badge key={acc} variant="secondary" className="text-[10px] sm:text-xs px-1.5 sm:px-2">
                  <Award className="w-2 h-2 mr-0.5 sm:mr-1" />
                  {acc}
                </Badge>
              ))}
              {hospital.accreditations.length > 2 && (
                <Badge variant="outline" className="text-[10px] sm:text-xs px-1.5">
                  +{hospital.accreditations.length - 2}
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
});
